import classNames from 'classnames'
import React from 'react'

import { getIconPath } from 'common'
import Data from 'data'
import Store from 'store'
import { WithGemTooltip, WithItemTooltip } from 'tooltip'

import { InventorySlotType } from '@/data/enums'
import { Item, ItemSocket, ItemTooltipOptions } from '@/types/item'
import { ItemId, ItemOrId } from '@/types/profile'
import { itemBaseIcon, itemGetBaseColor, itemGetName } from 'logic/item'
import { getGemColor } from 'logic/mods'
import './Paperdoll.scss'

function PaperdollSocket({ socket }: { socket: ItemSocket }) {
  const data = socket.gem && Data.gems[socket.gem.id]
  return (
    <div
      className={classNames(`poe-SocketImage poe-socket-${socket.color}`, data && `poe-gem-${getGemColor(data)}`, {
        'poe-socket-support': data && data.is_support
      })}>
      {socket.gem ? (
        <WithGemTooltip gem={socket.gem}>
          <div className="poe-image" />
        </WithGemTooltip>
      ) : (
        <div className="poe-image" />
      )}
      <div className={classNames('poe-socket-link', { 'poe-active': socket.link })} />
    </div>
  )
}

function PaperdollAbyssSocket({ socket }: { socket: ItemId }) {
  const item = Store.useItem(socket)
  return (
    <div className={classNames(`poe-SocketImage poe-socket-abyss`, item && `poe-gem-abyss`)}>
      {item ? (
        <WithItemTooltip item={item}>
          <div className="poe-image" />
        </WithItemTooltip>
      ) : (
        <div className="poe-image" />
      )}
    </div>
  )
}

function PaperdollSockets({ item }: { item: Item }) {
  if (!item.sockets?.length && !item.jewels?.length) return null
  const base = item && Data.items[item.base]
  const rowSize = Math.min(2, base ? base.inventory_width : 2)
  const sockets = [...item.sockets, ...(item.jewels ?? [])]
  const rows = [...Array(Math.ceil(sockets.length / rowSize))].map((_, i) =>
    sockets.slice(i * rowSize, i * rowSize + rowSize)
  )
  return (
    <div className="poe-socket-container">
      <div>
        {rows.map((row, i) => (
          <div key={i} className="poe-SocketRow">
            {row.map((socket, i) =>
              socket != null && typeof socket === 'object' ? (
                <PaperdollSocket key={i} socket={socket} />
              ) : (
                <PaperdollAbyssSocket key={i} socket={socket} />
              )
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function ItemLabel({ item }: { item: Item }) {
  return (
    <>
      <div className={`poe-label poe-item-${item.rarity}`}>{itemGetName(item)}</div>
      {['rare', 'unique', 'relic'].includes(item.rarity) && (
        <div className={`poe-label poe-item-${item.rarity}`}>{Data.items[item.base].name}</div>
      )}
    </>
  )
}

export function ItemDisplay({
  item,
  options,
  label,
  noSockets
}: {
  item: Item
  options?: ItemTooltipOptions
  label?: boolean
  noSockets?: boolean
}) {
  const data = item && ((item.unique && Data.uniques[item.unique]) || Data.items[item.base])
  const base = item && Data.items[item.base]
  if (!base) return null
  const icon = itemBaseIcon(data, item.map_tier, item.variant)
  return (
    <WithItemTooltip item={item} options={options}>
      <div className="poe-ItemDisplay-wrapper">
        <div className={`poe-ItemDisplay poe-iw${base.inventory_width} poe-ih${base.inventory_height}`}>
          {!!icon && <div className="poe-img" style={{ backgroundImage: `url(${getIconPath(icon)})` }} />}
          {!noSockets && <PaperdollSockets item={item} />}
        </div>
        {!!label && <ItemLabel item={item} />}
      </div>
    </WithItemTooltip>
  )
}

export function PaperdollSlot({
  slot,
  id,
  mirror,
  active,
  onClick
}: {
  slot: string
  id: ItemOrId
  mirror?: ItemOrId
  active?: boolean
  onClick?: () => void
  mini?: boolean
}) {
  const actualItem = Store.useItem(id)
  const mirrorItem = Store.useItem(mirror)
  const item =
    actualItem ?? (mirrorItem && Data.items[mirrorItem.base]?.tags.includes('twohand') ? mirrorItem : undefined)
  const data = item && ((item.unique && Data.uniques[item.unique]) || Data.items[item.base])
  const base = item && Data.items[item.base]
  const className = classNames(
    `poe-PaperdollSlot poe-slot-${slot}`,
    base && `poe-item-${itemGetBaseColor(base) || item.rarity || 'normal'}`,
    {
      'poe-empty': !base,
      'poe-active': active,
      'poe-mirror': item && !actualItem
    }
  )
  if (!base) return <div className={className} onClick={onClick} />
  const icon = data ? itemBaseIcon(data, item.map_tier, item.variant) : undefined
  return (
    <WithItemTooltip
      item={item}
      disabled={!actualItem}
      actions={
        onClick && [
          {
            text: 'Edit',
            action: onClick
          }
        ]
      }>
      <div className={className} onClick={onClick}>
        <div className={`poe-ItemDisplay poe-iw${base.inventory_width} poe-ih${base.inventory_height}`}>
          {!!icon && <div className="poe-img" style={{ backgroundImage: `url(${getIconPath(icon)})` }} />}
          {actualItem != null && <PaperdollSockets item={item} />}
        </div>
      </div>
    </WithItemTooltip>
  )
}

export default function Paperdoll({
  items,
  selected,
  onSelect,
  style
}: {
  items: {
    [slot in InventorySlotType]?: ItemOrId
  }
  selected?: InventorySlotType
  onSelect?: (slot: InventorySlotType) => void
  style?: React.CSSProperties
}) {
  const [swapped, setSwapped] = React.useState(false)
  const weaponSlot = swapped ? 'Weapon2' : 'Weapon'
  const offhandSlot = swapped ? 'Offhand2' : 'Offhand'
  const weaponSlot2 = swapped ? 'Weapon' : 'Weapon2'
  const offhandSlot2 = swapped ? 'Offhand' : 'Offhand2'
  const onSwap = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setSwapped(!swapped)
      if (onSelect) {
        if (!swapped && selected === 'Weapon') onSelect('Weapon2')
        if (!swapped && selected === 'Offhand') onSelect('Offhand2')
        if (swapped && selected === 'Weapon2') onSelect('Weapon')
        if (swapped && selected === 'Offhand2') onSelect('Offhand')
      }
    },
    [swapped, selected, onSelect]
  )
  const onSwapWeapon = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault()
      event?.stopPropagation()
      setSwapped(!swapped)
      onSelect?.(swapped ? 'Weapon' : 'Weapon2')
    },
    [swapped, onSelect]
  )
  const onSwapOffhand = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault()
      event?.stopPropagation()
      setSwapped(!swapped)
      onSelect?.(swapped ? 'Offhand' : 'Offhand2')
    },
    [swapped, onSelect]
  )
  return (
    <div
      className={classNames(`poe-Paperdoll`, {
        'poe-swapped': swapped,
        'poe-editing': onSelect
      })}
      style={style}>
      {(
        [
          'Amulet',
          'Belt',
          'BodyArmour',
          'Boots',
          'Gloves',
          'Helm',
          'Ring',
          'Ring2',
          'Flask1',
          'Flask2',
          'Flask3',
          'Flask4',
          'Flask5'
        ] as const
      ).map((slot) => (
        <PaperdollSlot
          key={slot}
          slot={slot}
          id={items[slot]}
          active={selected === slot}
          onClick={() => onSelect?.(slot)}
        />
      ))}
      <PaperdollSlot
        slot="Weapon"
        id={items[weaponSlot]}
        active={selected === weaponSlot}
        onClick={() => onSelect?.(weaponSlot)}
      />
      <PaperdollSlot
        slot="Offhand"
        id={items[offhandSlot]}
        active={selected === offhandSlot}
        onClick={() => onSelect?.(offhandSlot)}
      />
      <PaperdollSlot slot="WeaponMini" id={items[weaponSlot2]} onClick={onSwapWeapon} mini />
      <PaperdollSlot slot="OffhandMini" id={items[offhandSlot2]} onClick={onSwapOffhand} mini />
      <div className="poe-SwapButton poe-weapon" onClick={onSwap} />
      <div className="poe-SwapButton poe-offhand" onClick={onSwap} />
      <div className="poe-JewelSlots">
        {(
          [
            'Jewel1',
            'Jewel2',
            'Jewel3',
            'Jewel4',
            'Jewel5',
            'Jewel6',
            'Jewel7',
            'Jewel8',
            'Jewel9',
            'Jewel10',
            'Jewel11',
            'Jewel12',
            'Jewel13',
            'Jewel14',
            'Jewel15',
            'Jewel16',
            'Jewel17',
            'Jewel18',
            'Jewel19',
            'Jewel20',
            'Jewel21',
            'Jewel22',
            'Jewel23',
            'Jewel24'
          ] as const
        ).map((slot) => (
          <PaperdollSlot
            key={slot}
            slot={slot}
            id={items[slot]}
            active={selected === slot}
            onClick={() => onSelect?.(slot)}
          />
        ))}
      </div>
    </div>
  )
}
